<template>
  <div class="admin-tournament-simulations">
    <!-- Shared player tag portal for this page -->
    <SharedPlayerTagPortal @onToggleParticipate="toggleRoundsParticipated" />

    <p class="panel-heading">Tournament Simulation</p>

    <div class="pt-4">
      <b-field label="Round state" class="has-text-left">
        <b-tabs type="is-toggle" :animated="false">
          <b-tab-item label="Edit">
            <div class="mt-2">
              <b-input
                type="textarea"
                placeholder="Paste round's data here to simulate"
                rows="10"
                v-model="copiedText"
              />
            </div>
          </b-tab-item>

          <b-tab-item label="JSON Viewer">
            <json-viewer :value="copiedJSON" v-if="copiedJSON"></json-viewer>
          </b-tab-item>
        </b-tabs>
      </b-field>

      <TournamentRounds
        v-if="tournament && tournament.id"
        :tournament="tournament"
        :rounds="rounds"
        :games="games"
        :players="players"
        @onPair="pairRound"
        @onSave="saveRound"
      ></TournamentRounds>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import {
  LocalFirestore,
  installHooks,
  uninstallHooks,
} from "vue-chess-shared/tournament/LocalFirestore";
import TournamentRounds from "@/components/admins/tournaments/TournamentRounds.vue";
import SharedPlayerTagPortal from "@/components/admins/SharedPlayerTagPortal.vue";
import JsonViewer from "vue-json-viewer";
import { functions } from "@/services/firebase";
import { TournamentManagerFactory } from "vue-chess-shared/tournament/TournamentManagerFactory";
import { createWithManager } from "@/components/admins/tournaments/TournamentManagerClientFactory.js";

const handleBBPPair = functions.httpsCallable("tournament-handleBBPPair");
const tournamentManager = TournamentManagerFactory.createForLocal(
  async (data) => (await handleBBPPair(data))?.data
);
const TournamentManagerClient = createWithManager(tournamentManager);

export default {
  components: {
    JsonViewer,
    SharedPlayerTagPortal,
    TournamentRounds,
  },

  name: "AdminTournamentSimulation",

  title: "Admin Tournament Simulation",

  data() {
    return {
      copiedText: null,
      copiedJSON: null,
      tournamentId: null,
      tournament: null,
      rounds: [],
      games: [],
      players: [],
    };
  },

  mixins: [TournamentManagerClient],

  watch: {
    copiedText() {
      this.parseAndLoadData();
    },
  },

  computed: {},

  methods: {
    parseAndLoadData: _.debounce(async function () {
      console.log("parseAndLoadData");

      LocalFirestore.reset();

      try {
        this.copiedJSON = JSON.parse(this.copiedText);
        const tournament = this.copiedJSON.tournament;
        const rounds = this.copiedJSON.rounds;
        const games = this.copiedJSON.games;
        const players = this.copiedJSON.players;

        this.tournamentId = tournament.id;

        const tournamentRef = LocalFirestore.collection("tournaments").doc(
          tournament.id
        );
        const roundsRef = tournamentRef.collection("rounds");
        const gamesRef = tournamentRef.collection("games");
        const playersRef = tournamentRef.collection("players");
        const matchesRef = LocalFirestore.collection("matches");

        if (tournament) {
          await tournamentRef.set(tournament);
        }

        if (rounds && rounds.length) {
          for (let i = 0; i < rounds.length; i++) {
            await roundsRef.doc(rounds[i].roundNumber).set(rounds[i]);
          }
        }

        if (games && games.length) {
          for (let i = 0; i < games.length; i++) {
            if (games[i].matchRef) {
              await matchesRef.doc(games[i].matchGUID).set(games[i].matchRef);
            }

            await gamesRef.doc(games[i].gameId).set({
              ...games[i],
              matchRef: games[i].matchRef
                ? matchesRef.doc(games[i].matchGUID)
                : null,
            });
          }
        }

        if (players && players.length) {
          for (let i = 0; i < players.length; i++) {
            await playersRef.doc(players[i].username).set(players[i]);
          }
        }
        await tournamentManager.scheduleRounds(tournament.id);

        // if the copied round is finished, then finish here to update it into local states
        const lastRound = rounds.at(-1);
        if (
          lastRound &&
          lastRound.finishTime &&
          tournament.currentRound === lastRound.roundNumber
        ) {
          await tournamentManager.finishRound(
            tournament.id,
            lastRound.roundNumber
          );
        }

        await this.$bind("tournament", tournamentRef);
        await this.$bind("players", playersRef);
        await this.$bind("rounds", roundsRef);
        await this.$bind("games", gamesRef);
      } catch (err) {
        console.error(err);
        this.tournament = null;
        this.rounds = [];
        this.games = [];
        this.players = [];
        this.copiedText = null;
        this.copiedJSON = null;
        this.$buefy.toast.open({
          message: "Invalid round state",
          type: "is-danger",
        });
      }
    }),
  },

  async mounted() {
    try {
      const jsonText = await navigator.clipboard.readText();

      if (!jsonText) {
        return;
      }

      this.copiedText = jsonText;
    } catch (err) {
      console.error(err);
      return;
    }
  },

  beforeCreate() {
    installHooks(this);
  },

  beforeDestroy() {
    uninstallHooks(this);
  },
};
</script>

<style>
.b-tabs .tab-content {
  padding: 0;
}
</style>
